import { useMemo } from "react";
import { useBrand, useBrandVoice, useBrandVoiceWorkbench } from "./useBrand";
import { useStrategy, useBrandStrategyWorkbench } from "./useStrategy";
import { buildBrandArtifactPath } from "../lib/brand-navigation";

export interface BrandDoc {
  id: string;
  title: string;
  summary: string;
  status: string;
  revision: string | number | null;
  href: string | null;
  ready: boolean;
  error: boolean;
}

function statusLabel(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  return value
    .split("_")
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ");
}

/**
 * Canonical brand-docs list (strategy + voice) shared by BrandDocsPage (main
 * content) and ShellSidePanel (the contextual docs list). Single source so the
 * page and the shell panel never drift.
 */
export function useBrandDocsList(brandId: string | null | undefined): {
  docs: BrandDoc[];
  docsReadyCount: number;
  docsLoading: boolean;
} {
  const id = brandId ?? undefined;
  const { isLoading: brandLoading } = useBrand(id);
  const { data: strategyData, isLoading: strategyLoading, isError: strategyError } = useStrategy(id);
  const { data: strategyWorkbench, isLoading: strategyWorkbenchLoading, isError: strategyWorkbenchError } =
    useBrandStrategyWorkbench(id);
  const { data: voiceData, isLoading: voiceLoading, isError: voiceError } = useBrandVoice(id);
  const { data: voiceWorkbench, isLoading: voiceWorkbenchLoading, isError: voiceWorkbenchError } =
    useBrandVoiceWorkbench(id);

  const docs = useMemo<BrandDoc[]>(
    () => [
      {
        id: "strategy",
        title: "Brand strategy",
        summary: strategyData?.data?.north_star ?? "Canonical positioning, offer framing, and strategic constraints.",
        status: statusLabel(strategyWorkbench?.data?.status, strategyData?.data ? "Draft" : "Missing"),
        revision: strategyWorkbench?.data?.manifest_version ?? null,
        href: brandId ? buildBrandArtifactPath(brandId, "strategy") : null,
        ready: !!strategyData?.data || !!strategyWorkbench?.data,
        error: !!(strategyError || strategyWorkbenchError),
      },
      {
        id: "voice",
        title: "Voice system",
        summary: voiceData?.data?.tone ?? "Tone guardrails, voice principles, and learned corrections.",
        status: statusLabel(voiceWorkbench?.data?.status, voiceData?.data ? "Configured" : "Missing"),
        revision: voiceWorkbench?.data?.manifest_version ?? null,
        href: brandId ? buildBrandArtifactPath(brandId, "voice") : null,
        ready: !!voiceData?.data || !!voiceWorkbench?.data,
        error: !!(voiceError || voiceWorkbenchError),
      },
    ],
    [
      brandId,
      strategyData?.data,
      strategyError,
      strategyWorkbench?.data,
      strategyWorkbenchError,
      voiceData?.data,
      voiceError,
      voiceWorkbench?.data,
      voiceWorkbenchError,
    ],
  );

  const docsLoading =
    brandLoading || strategyLoading || strategyWorkbenchLoading || voiceLoading || voiceWorkbenchLoading;
  const docsReadyCount = docs.filter((doc) => doc.ready).length;

  return { docs, docsReadyCount, docsLoading };
}
